import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Stack,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { createProjectWorkflowFlow, fetchProjectWorkflowFlows } from '../../Utils/actionsApi';
import { useWebSocket } from '../WebSocketContext';
import { hasUserTagVariableReservedChars, normalizeTagVariable } from '../../Utils/tagUtils';

const DEFAULT_LOOP_TAG_NAME = 'loopItem';
const DEFAULT_LOOP_TAG_DESCRIPTION = 'the item here';

const parseFlowId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const createLoopTagRow = (name = '', description = '', id = null) => ({
  id: id || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  name,
  description
});

const normalizeSourceTagValue = (value) => {
  return normalizeTagVariable(value);
};

const normalizeLoopTagName = (value) => {
  return normalizeTagVariable(value);
};

// Keep whitespace while the controlled input is being edited. Trimming here
// would remove a trailing space after every keystroke and make it impossible
// to type the next word in the description.
const normalizeLoopTagDescription = (value) => String(value ?? '');

const isValidLoopTagName = (value) => {
  const normalized = String(value || '').trim();
  return /^[^{}\s]+$/.test(normalized) && !hasUserTagVariableReservedChars(normalized);
};

const normalizeLoopTagRow = (value) => {
  if (typeof value === 'string') {
    return createLoopTagRow(normalizeLoopTagName(value), DEFAULT_LOOP_TAG_DESCRIPTION);
  }

  if (!value || typeof value !== 'object') {
    return createLoopTagRow('', DEFAULT_LOOP_TAG_DESCRIPTION);
  }

  return createLoopTagRow(
    normalizeLoopTagName(value.name || value.variable || value.tagName || value.tag_name || value.value || ''),
    normalizeLoopTagDescription(
      value.description !== undefined && value.description !== null
        ? value.description
        : (value.label !== undefined && value.label !== null ? value.label : '')
    ),
    value.id || value._id || value.key || null
  );
};

const normalizeLoopTagRows = (value) => {
  const rawValue = Array.isArray(value)
    ? value
    : (() => {
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch (error) {
            return [];
          }
        }
        return [];
      })();

  const normalized = rawValue.map(normalizeLoopTagRow);
  return normalized.length > 0 ? normalized : [createLoopTagRow(DEFAULT_LOOP_TAG_NAME, DEFAULT_LOOP_TAG_DESCRIPTION)];
};

const compactLoopTagRows = (rows) => rows.filter((row) => (
  String(row?.name || '').trim().length > 0 ||
  String(row?.description || '').trim().length > 0
));

const serializeLoopTagRows = (rows) => compactLoopTagRows(rows).map((row) => ({
  name: normalizeLoopTagName(row.name),
  description: normalizeLoopTagDescription(row.description).trim()
}));

const getNormalizedLoopTagNames = (rows) => serializeLoopTagRows(rows).map((row) => row.name).filter(Boolean);

const StepSection = ({ icon, stepLabel, title, description, children }) => (
  <Box sx={{ mb: 2.75 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '8px',
          bgcolor: '#eef2ff',
          color: '#5757b0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
          {stepLabel}
        </Typography>
        <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.95rem' }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ color: '#666', fontSize: '0.82rem', mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ pl: { xs: 0, sm: 6 } }}>
      {children}
    </Box>
  </Box>
);

const ActionsLoopTriggerFlow = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  workflowFlows = [],
  currentFlowId = null,
  onCreateWorkflowFlow
}) => {
  const { t } = useTranslation();
  const { subscribeToMessages } = useWebSocket();
  const [localFlows, setLocalFlows] = useState([]);
  const [targetFlowId, setTargetFlowId] = useState('');
  const [sourceTagInput, setSourceTagInput] = useState('');
  const [instruction, setInstruction] = useState('');
  const [loopTags, setLoopTags] = useState(() => [
    createLoopTagRow(DEFAULT_LOOP_TAG_NAME, DEFAULT_LOOP_TAG_DESCRIPTION)
  ]);
  const [createFlowDialogOpen, setCreateFlowDialogOpen] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [createFlowError, setCreateFlowError] = useState('');
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [pendingCreatedFlowName, setPendingCreatedFlowName] = useState('');

  const sourceFlowId = useMemo(() => parseFlowId(currentFlowId), [currentFlowId]);

  useEffect(() => {
    if (!caseid) {
      return;
    }
    let cancelled = false;
    fetchProjectWorkflowFlows(caseid)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        const flows = Array.isArray(payload?.flows) ? payload.flows : [];
        setLocalFlows(flows);
        if (pendingCreatedFlowName) {
          const matchingCreatedFlow = flows.find((flow) => (
            String(flow?.name || '').trim() === pendingCreatedFlowName
          ));
          const matchingCreatedFlowId = parseFlowId(matchingCreatedFlow?.id);
          if (matchingCreatedFlowId) {
            setTargetFlowId(String(matchingCreatedFlowId));
          }
          setPendingCreatedFlowName('');
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Failed to load workflow flows:', error);
          setLocalFlows([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [caseid, pendingCreatedFlowName]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage?.command === 'templateChangeDecisionRequired' && isCreatingFlow) {
        setIsCreatingFlow(false);
        setCreateFlowDialogOpen(false);
        return;
      }

      if (parsedMessage?.command !== 'createWorkflowFlowResponse' || (!isCreatingFlow && !pendingCreatedFlowName)) {
        return;
      }

      const payload = parsedMessage?.data || {};
      if (payload.status === 'success' && payload.flow?.id !== undefined) {
        const createdFlow = payload.flow;
        if (
          pendingCreatedFlowName &&
          String(createdFlow.name || '').trim() !== pendingCreatedFlowName
        ) {
          return;
        }
        const createdFlowId = parseFlowId(createdFlow.id);
        if (createdFlowId) {
          setLocalFlows((previousFlows) => {
            const withoutDuplicate = previousFlows.filter((flow) => String(flow?.id) !== String(createdFlowId));
            return [...withoutDuplicate, createdFlow];
          });
          setTargetFlowId(String(createdFlowId));
        }
        setCreateFlowDialogOpen(false);
        setCreateFlowError('');
        setNewFlowName('');
        setIsCreatingFlow(false);
        setPendingCreatedFlowName('');
        return;
      }

      if (payload.status === 'error') {
        setCreateFlowError(
          payload.message || t('workflow.looptriggerflow.create_flow_error', 'Could not create the flow. Try again.')
        );
        setIsCreatingFlow(false);
        setPendingCreatedFlowName('');
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, caseid, isCreatingFlow, pendingCreatedFlowName, t]);

  const selectableFlows = useMemo(() => {
    const source = Array.isArray(workflowFlows) && workflowFlows.length > 0 ? workflowFlows : localFlows;
    return source
      .map((flow) => {
        const id = parseFlowId(flow?.id);
        if (!id) {
          return null;
        }
        return {
          id,
          name: String(flow?.name || `Flow ${id}`),
          orderIndex: Number(flow?.orderIndex || 0)
        };
      })
      .filter((flow) => flow && flow.id !== sourceFlowId)
      .sort((a, b) => {
        if (a.orderIndex !== b.orderIndex) {
          return a.orderIndex - b.orderIndex;
        }
        return a.id - b.id;
      });
  }, [workflowFlows, localFlows, sourceFlowId]);

  useEffect(() => {
    if (parseFlowId(targetFlowId)) {
      return;
    }
    if (selectableFlows.length > 0) {
      setTargetFlowId(String(selectableFlows[0].id));
    }
  }, [targetFlowId, selectableFlows]);

  const selectedTargetFlowId = parseFlowId(targetFlowId);
  const selectedTargetFlow = selectableFlows.find((flow) => flow.id === selectedTargetFlowId) || null;
  const selectedTargetExists = selectableFlows.some((flow) => flow.id === selectedTargetFlowId);
  const isTargetMissing = !selectedTargetFlowId || !selectedTargetExists;
  const normalizedSourceTag = normalizeSourceTagValue(sourceTagInput);
  const normalizedLoopTags = useMemo(() => normalizeLoopTagRows(loopTags), [loopTags]);
  const loopTagNames = useMemo(() => getNormalizedLoopTagNames(loopTags), [loopTags]);
  const loopTagTokens = loopTagNames.map((tagName) => `{${tagName}}`);
  const summarySource = normalizedSourceTag
    ? `{${normalizedSourceTag}}`
    : t('workflow.looptriggerflow.summary_missing_source', 'the selected information');
  const summaryItems = instruction.trim()
    || t('workflow.looptriggerflow.summary_missing_items', 'the items you describe');
  const summaryFlow = selectedTargetFlow?.name
    || t('workflow.looptriggerflow.summary_missing_flow', 'the selected flow');
  const summaryTagTokens = loopTagTokens.length > 0
    ? loopTagTokens.join(', ')
    : t('workflow.looptriggerflow.summary_missing_tags', 'the tags you define');
  const normalizedLoopTagNames = normalizedLoopTags.map((tag) => tag.name);
  const hasLoopTagNames = normalizedLoopTagNames.length > 0;
  const invalidLoopTagNames = normalizedLoopTags.filter((tag) => !isValidLoopTagName(tag.name)).map((tag) => tag.name);
  const duplicateLoopTagNames = normalizedLoopTagNames.filter((tagName, index, array) => (
    tagName && array.findIndex((candidate) => candidate.toLowerCase() === tagName.toLowerCase()) !== index
  ));
  const conflictingLoopTagNames = normalizedLoopTags
    .filter((tag) => Boolean(normalizedSourceTag) && tag.name === normalizedSourceTag)
    .map((tag) => tag.name);
  const isConfigurationIncomplete = (
    !normalizedSourceTag ||
    !instruction.trim() ||
    !hasLoopTagNames ||
    invalidLoopTagNames.length > 0 ||
    duplicateLoopTagNames.length > 0 ||
    conflictingLoopTagNames.length > 0 ||
    isTargetMissing
  );

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'looptriggerflow',
    onSaveStatusChange,
    customComparison: (initial) => {
      const initialTarget = parseFlowId(initial.targetFlowId || initial.target_flow_id);
      const currentTarget = parseFlowId(targetFlowId);
      const initialLoopTags = serializeLoopTagRows(
        normalizeLoopTagRows(initial.loopTags || initial.loop_tags || (
          initial.loopTagName || initial.loop_tag_name
            ? [{ name: initial.loopTagName || initial.loop_tag_name, description: initial.loopTagDescription || initial.loop_tag_description || DEFAULT_LOOP_TAG_DESCRIPTION }]
            : []
        ))
      );
      const currentLoopTags = serializeLoopTagRows(loopTags);
      return (
        initialTarget === currentTarget &&
        normalizedSourceTag === normalizeSourceTagValue(initial.sourceTag || initial.source_tag || '') &&
        instruction === (initial.instruction || '') &&
        JSON.stringify(initialLoopTags) === JSON.stringify(currentLoopTags)
      );
    },
    customSaveData: () => ({
      targetFlowId: parseFlowId(targetFlowId),
      sourceTag: normalizedSourceTag,
      instruction,
      loopTags: serializeLoopTagRows(loopTags),
      loopTagName: loopTagNames[0] || DEFAULT_LOOP_TAG_NAME,
      useConditions: true,
      conditions
    }),
    loadDataFunction: (data) => {
      const savedTarget = parseFlowId(
        data?.customData?.targetFlowId || data?.customData?.target_flow_id
      );
      setTargetFlowId(savedTarget ? String(savedTarget) : '');
      setSourceTagInput(
        (() => {
          const normalizedSource = normalizeSourceTagValue(
            data?.customData?.sourceTag || data?.customData?.source_tag || ''
          );
          return normalizedSource ? `{${normalizedSource}}` : '';
        })()
      );
      setInstruction(data?.customData?.instruction || '');
      const loadedLoopTags = normalizeLoopTagRows(
        data?.customData?.loopTags ||
        data?.customData?.loop_tags ||
        (
          data?.customData?.loopTagName || data?.customData?.loop_tag_name
            ? [{
                name: data?.customData?.loopTagName || data?.customData?.loop_tag_name,
                description: data?.customData?.loopTagDescription || data?.customData?.loop_tag_description || DEFAULT_LOOP_TAG_DESCRIPTION
              }]
            : []
        )
      );
      setLoopTags(loadedLoopTags);
    }
  });

  const handleOpenCreateFlowDialog = () => {
    setNewFlowName(t('workflow.looptriggerflow.new_flow_default', 'Process each item'));
    setCreateFlowError('');
    setCreateFlowDialogOpen(true);
  };

  const handleCloseCreateFlowDialog = () => {
    if (isCreatingFlow) {
      return;
    }
    setCreateFlowDialogOpen(false);
    setCreateFlowError('');
  };

  const handleCreateFlow = async () => {
    const flowName = String(newFlowName || '').trim();
    if (!flowName) {
      setCreateFlowError(t('workflow.looptriggerflow.create_flow_name_required', 'Flow name is required.'));
      return;
    }

    const payload = {
      name: flowName,
      auto_run_enabled: false,
      trigger_mode: 'disabled',
      auto_run_frequency_value: 0,
      auto_run_frequency_unit: 'minutes'
    };
    setIsCreatingFlow(true);
    setCreateFlowError('');

    try {
      const createResult = await Promise.resolve(
        typeof onCreateWorkflowFlow === 'function'
          ? onCreateWorkflowFlow(payload)
          : (caseid ? createProjectWorkflowFlow(caseid, payload) : false)
      );

      if (!createResult) {
        setCreateFlowError(
          t('workflow.looptriggerflow.create_flow_connection_error', 'Cannot create a flow right now. Check the connection and try again.')
        );
        setIsCreatingFlow(false);
        return;
      }

      if (createResult === true) {
        setPendingCreatedFlowName(flowName);
        return;
      }

      if (createResult.status === 'decision_required') {
        setIsCreatingFlow(false);
        setCreateFlowDialogOpen(false);
        setPendingCreatedFlowName('');
        return;
      }

      if (createResult.status !== 'success') {
        setCreateFlowError(
          createResult.message
            || t('workflow.looptriggerflow.create_flow_error', 'Could not create the flow. Try again.')
        );
        setIsCreatingFlow(false);
        setPendingCreatedFlowName('');
        return;
      }

      const createdFlow = createResult.flow || {};
      const createdFlowId = parseFlowId(createdFlow.id);
      if (createdFlowId) {
        setTargetFlowId(String(createdFlowId));
      }

      if (Array.isArray(createResult.flows)) {
        setLocalFlows(createResult.flows);
      } else if (createdFlowId) {
        setLocalFlows((previousFlows) => {
          const withoutDuplicate = previousFlows.filter((flow) => String(flow?.id) !== String(createdFlowId));
          return [...withoutDuplicate, createdFlow];
        });
      }

      setCreateFlowDialogOpen(false);
      setNewFlowName('');
      setCreateFlowError('');
      setIsCreatingFlow(false);
      setPendingCreatedFlowName('');
    } catch (error) {
      setCreateFlowError(
        error?.message || t('workflow.looptriggerflow.create_flow_error', 'Could not create the flow. Try again.')
      );
      setIsCreatingFlow(false);
      setPendingCreatedFlowName('');
    }
  };

  return (
    <ActionSettingsLayout
      title={t('workflow.looptriggerflow', 'Find Items and Repeat Flow')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || isConfigurationIncomplete}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
    >
      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden'
        }}
      >
        <CardHeader
          title={
            <Typography
              sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Box
                component="span"
                sx={{
                  bgcolor: '#5757b0',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)'
                }}
              >
                <AutorenewIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                {t('workflow.looptriggerflow.badge', 'REPEAT')}
              </Box>
              {t('workflow.looptriggerflow.settings_title', 'Repeat a flow for each item found')}
            </Typography>
          }
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 }
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Typography sx={{ mb: 2, fontSize: '0.9rem', color: '#666' }}>
            {t(
              'workflow.looptriggerflow.description',
              'Find multiple items in a tag, then run another flow once for each item. Use it for patients, invoices, products, reservations, or any repeated information.'
            )}
          </Typography>

          <Box
            sx={{
              border: '1px solid #dbe3f0',
              backgroundColor: '#f8fbff',
              borderRadius: '8px',
              p: { xs: 1.25, sm: 1.75 },
              mb: 2.75
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                color: '#344054',
                fontWeight: 700,
                fontSize: '0.9rem',
                mb: 0.75
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: '1rem', color: '#15803d' }} />
              {t('workflow.looptriggerflow.summary_title', 'What will happen')}
            </Typography>
            <Typography sx={{ color: '#475467', fontSize: '0.86rem', lineHeight: 1.55 }}>
              {t('workflow.looptriggerflow.summary', {
                defaultValue: 'DataUnfold will read {{source}}, find {{items}}, then run {{flow}} once for each result. The repeated flow can use {{tagTokens}} for the current result.',
                source: summarySource,
                items: summaryItems,
                flow: summaryFlow,
                tagTokens: summaryTagTokens
              })}
            </Typography>
          </Box>

          <StepSection
            icon={<SearchIcon sx={{ fontSize: '1.1rem' }} />}
            stepLabel={t('workflow.looptriggerflow.step_source', 'Step 1')}
            title={t('workflow.looptriggerflow.source_tag', 'Choose the information to scan')}
            description={t(
              'workflow.looptriggerflow.source_tag_help',
              'Pick the tag that contains the text, list, email, report, or extracted data to inspect.'
            )}
          >
            <Box
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                minHeight: '64px'
              }}
            >
              <TagEditor
                caseid={caseid}
                value={sourceTagInput}
                onChange={setSourceTagInput}
                tags={tags}
                placeholder={t('workflow.looptriggerflow.select_source_tag', 'Select the tag to scan')}
                singleLine
              />
            </Box>
          </StepSection>

          <StepSection
            icon={<FormatListBulletedIcon sx={{ fontSize: '1.1rem' }} />}
            stepLabel={t('workflow.looptriggerflow.step_instruction', 'Step 2')}
            title={t('workflow.looptriggerflow.instruction', 'Describe the item and define tags')}
            description={t(
              'workflow.looptriggerflow.instruction_help',
              'Tell DataUnfold what counts as one result, then define every tag that should be filled for that result.'
            )}
          >
            <Box
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                minHeight: '110px',
                mb: 1.75
              }}
            >
              <TagEditor
                caseid={caseid}
                value={instruction}
                onChange={setInstruction}
                tags={tags}
                placeholder={t(
                  'workflow.looptriggerflow.instruction_placeholder',
                  'Example: Find each patient name in this report. Return one patient per result.'
                )}
                multiline
                minRows={3}
              />
            </Box>

            <Typography sx={{ color: '#666', fontSize: '0.82rem', mb: 1.25, fontWeight: 700 }}>
              {t('workflow.looptriggerflow.tags_title', 'Tags')}
            </Typography>
            <Typography sx={{ color: '#666', fontSize: '0.82rem', mb: 1.5 }}>
              {t(
                'workflow.looptriggerflow.tags_description',
                'Define every tag that should be available inside the repeated flow. This configuration is required.'
              )}
            </Typography>

            <Stack spacing={1.5}>
              {normalizedLoopTags.map((tag, index) => {
                const isInvalid = !isValidLoopTagName(tag.name);
                const isConflict = Boolean(normalizedSourceTag) && tag.name === normalizedSourceTag;
                const isDuplicate = normalizedLoopTagNames.findIndex(
                  (candidate) => candidate.toLowerCase() === tag.name.toLowerCase()
                ) !== index;

                return (
                  <Box
                    key={tag.id}
                    sx={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      p: 1.25,
                      backgroundColor: '#fff'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: '#344054', fontSize: '0.82rem' }}>
                        {t('workflow.looptriggerflow.loop_tag_row_title', {
                          defaultValue: 'Tag {{index}}',
                          index: index + 1
                        })}
                      </Typography>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => {
                          setLoopTags((current) => {
                            if (current.length <= 1) {
                              return [createLoopTagRow(DEFAULT_LOOP_TAG_NAME, DEFAULT_LOOP_TAG_DESCRIPTION)];
                            }
                            return current.filter((_, currentIndex) => currentIndex !== index);
                          });
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('workflow.looptriggerflow.loop_tags_remove_button', 'Remove tag')}
                      </Button>
                    </Box>

                    <Stack spacing={1.25}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('workflow.looptriggerflow.loop_tags_name_label', 'Tag name')}
                        value={tag.name}
                        onChange={(event) => {
                          const nextName = normalizeLoopTagName(event.target.value);
                          setLoopTags((current) => current.map((currentTag, currentIndex) => (
                            currentIndex === index ? { ...currentTag, name: nextName } : currentTag
                          )));
                        }}
                        placeholder={t('workflow.looptriggerflow.loop_tags_name_placeholder', 'Example: patient_first_name')}
                        error={isInvalid || isConflict || isDuplicate}
                        helperText={
                          isConflict
                            ? t('workflow.looptriggerflow.loop_tags_conflict', 'Tag names must be different from the source tag.')
                            : isDuplicate
                              ? t('workflow.looptriggerflow.loop_tags_duplicate', 'Each tag name must be unique.')
                              : isInvalid
                                ? t('workflow.looptriggerflow.loop_tags_invalid', 'Tag names cannot contain spaces or reserved metadata suffixes such as _reason or _coordinates.')
                                : t('workflow.looptriggerflow.loop_tags_name_help', 'This name will be available inside the repeated flow.')
                        }
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label={t('workflow.looptriggerflow.loop_tags_description_label', 'Description')}
                        value={tag.description}
                        onChange={(event) => {
                          const nextDescription = String(event.target.value ?? '');
                          setLoopTags((current) => current.map((currentTag, currentIndex) => (
                            currentIndex === index ? { ...currentTag, description: nextDescription } : currentTag
                          )));
                        }}
                        placeholder={t('workflow.looptriggerflow.loop_tags_description_placeholder', 'What this tag represents')}
                      />

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<AltRouteIcon />}
                          label={t('workflow.looptriggerflow.loop_tag_chip', {
                            defaultValue: 'Use in repeated flow: {{tagToken}}',
                            tagToken: `{${tag.name || DEFAULT_LOOP_TAG_NAME}}`
                          })}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Stack>
                  </Box>
                );
              })}

              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setLoopTags((current) => [...current, createLoopTagRow('', '')])}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: '8px' }}
              >
                {t('workflow.looptriggerflow.loop_tags_add_button', 'Add tag')}
              </Button>
            </Stack>

            <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 1.25, fontStyle: 'italic' }}>
              {t('workflow.looptriggerflow.help', {
                defaultValue: 'The repeated flow receives one result at a time through {{tagTokens}}. In project scope, the source information is read from project tags.',
                tagTokens: summaryTagTokens
              })}
            </Typography>
          </StepSection>

          <StepSection
            icon={<PlayArrowIcon sx={{ fontSize: '1.15rem' }} />}
            stepLabel={t('workflow.looptriggerflow.step_target', 'Step 3')}
            title={t('workflow.looptriggerflow.target_flow', 'Choose the flow to repeat')}
            description={t(
              'workflow.looptriggerflow.target_flow_help',
              'This flow will run once for every item found in step 2.'
            )}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="looptriggerflow-target-label">
                {t('workflow.looptriggerflow.target_flow_select', 'Flow to repeat')}
              </InputLabel>
              <Select
                labelId="looptriggerflow-target-label"
                value={selectedTargetExists ? String(selectedTargetFlowId) : ''}
                label={t('workflow.looptriggerflow.target_flow_select', 'Flow to repeat')}
                onChange={(event) => setTargetFlowId(event.target.value)}
              >
                <MenuItem value="" disabled>
                  {t('workflow.looptriggerflow.select_target_placeholder', 'Select the flow to run for each result')}
                </MenuItem>
                {selectableFlows.map((flow) => (
                  <MenuItem key={flow.id} value={String(flow.id)}>
                    {flow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#666', fontSize: '0.78rem', flex: '1 1 240px' }}>
                {t(
                  'workflow.looptriggerflow.create_flow_hint',
                  'Need a new flow for this repeated work? Create it here, then add its actions after saving.'
                )}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateFlowDialog}
                sx={{ textTransform: 'none', borderRadius: '8px', flexShrink: 0 }}
              >
                {t('workflow.looptriggerflow.create_flow_button', 'Create flow')}
              </Button>
            </Box>
          </StepSection>

          {selectableFlows.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.no_available_flows',
                'Create another flow first. This action needs a second flow to run for each item found.'
              )}
            </Alert>
          )}

          {isTargetMissing && selectableFlows.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.missing_target_warning',
                'Choose the flow to repeat before saving.'
              )}
            </Alert>
          )}

          {conflictingLoopTagNames.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.loop_tags_conflict',
                'This name must be different from the tag being scanned.'
              )}
            </Alert>
          )}

          {duplicateLoopTagNames.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.loop_tags_duplicate',
                'Each tag name must be unique.'
              )}
            </Alert>
          )}

          {invalidLoopTagNames.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t(
                'workflow.looptriggerflow.loop_tags_invalid',
                'Tag names cannot contain spaces or reserved metadata suffixes such as _reason or _coordinates.'
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createFlowDialogOpen}
        onClose={handleCloseCreateFlowDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('workflow.looptriggerflow.create_flow_dialog_title', 'Create a flow to repeat')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666', fontSize: '0.86rem', mb: 2 }}>
            {t(
              'workflow.looptriggerflow.create_flow_dialog_help',
              'This flow will be created without an automatic trigger, because this action will run it once for each item found.'
            )}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('workflow.looptriggerflow.create_flow_name_label', 'Flow name')}
            value={newFlowName}
            onChange={(event) => {
              setNewFlowName(event.target.value);
              setCreateFlowError('');
            }}
            disabled={isCreatingFlow}
          />
          {createFlowError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createFlowError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCreateFlowDialog} disabled={isCreatingFlow}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleCreateFlow} variant="contained" disabled={isCreatingFlow}>
            {isCreatingFlow
              ? t('workflow.looptriggerflow.create_flow_creating', 'Creating...')
              : t('workflow.looptriggerflow.create_flow_submit', 'Create flow')}
          </Button>
        </DialogActions>
      </Dialog>
    </ActionSettingsLayout>
  );
};

export { normalizeLoopTagRow, serializeLoopTagRows };

export default React.memo(ActionsLoopTriggerFlow);
